import React from "react";
import PropTypes from "prop-types";

import Constants from "Helpers/Constants";
import { BRAND_GROUPS } from "Helpers/ContentValidation";

import {
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import StringListField from "components/ContentForms/StringListField";

/**
 * Every editable field of a brand page, shared by the Add and Update screens so
 * the two can never drift apart.
 *
 * The parent owns the values and reports each edit through `onChange(name, value)`.
 */
const BrandFormFields = ({
  values,
  errors,
  onChange,
  onFile,
  parentOptions,
  existingLogo,
  isUpdate,
}) => {
  const text = (name, label, options = {}) => (
    <TextField
      fullWidth
      label={label}
      value={values[name] ?? ""}
      onChange={(event) => onChange(name, event.target.value)}
      error={Boolean(errors[name])}
      helperText={errors[name] || options.helperText || ""}
      multiline={Boolean(options.rows)}
      rows={options.rows}
      placeholder={options.placeholder}
      required={options.required}
    />
  );

  return (
    <Grid container spacing={2}>
      {/* ---------------------------------------------------------- Identity */}
      <Grid item xs={12}>
        <MDTypography variant="h6">Brand identity</MDTypography>
        <MDTypography variant="caption" color="text">
          How the brand is identified on the website and in this panel.
        </MDTypography>
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("name", "Brand name", { required: true })}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("slug", "URL key", {
          helperText: isUpdate
            ? "Changing this changes the address the website looks the brand up by"
            : "Left blank, it is generated from the brand name",
          placeholder: "value-nest",
        })}
      </Grid>

      <Grid item xs={12} sm={4}>
        <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
          Listing group
        </MDTypography>
        <Select
          fullWidth
          value={values.group || "brand"}
          onChange={(event) => onChange("group", event.target.value)}
          sx={{ height: "45px" }}
        >
          {BRAND_GROUPS.map((group) => (
            <MenuItem key={group.value} value={group.value}>
              {group.label}
            </MenuItem>
          ))}
        </Select>
        {errors.group ? (
          <MDTypography variant="caption" color="error">
            {errors.group}
          </MDTypography>
        ) : null}
      </Grid>

      <Grid item xs={12} sm={4}>
        <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
          Parent brand
        </MDTypography>
        <Select
          fullWidth
          value={values.parent_brand || ""}
          onChange={(event) => onChange("parent_brand", event.target.value)}
          displayEmpty
          sx={{ height: "45px" }}
        >
          <MenuItem value="">None — this is a top-level brand</MenuItem>
          {parentOptions.map((option) => (
            <MenuItem key={option._id} value={option._id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        <MDTypography variant="caption" color="text">
          Use for sub-brands (Happy Life and Lara sit under Versele-Laga)
        </MDTypography>
        {errors.parent_brand ? (
          <MDTypography variant="caption" color="error" display="block">
            {errors.parent_brand}
          </MDTypography>
        ) : null}
      </Grid>

      <Grid item xs={12} sm={4}>
        <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
          Status
        </MDTypography>
        <Select
          fullWidth
          value={values.status || "active"}
          onChange={(event) => onChange("status", event.target.value)}
          sx={{ height: "45px" }}
        >
          <MenuItem value="active">Active — visible on the website</MenuItem>
          <MenuItem value="inactive">Inactive — hidden</MenuItem>
        </Select>
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          type="number"
          label="Display order"
          value={values.sort_order ?? 0}
          onChange={(event) => onChange("sort_order", event.target.value)}
          error={Boolean(errors.sort_order)}
          helperText={errors.sort_order || "Lower numbers list first"}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        {text("page_route", "Website page route", { placeholder: "/value-nest" })}
      </Grid>

      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(values.show_on_all_products)}
              onChange={(event) => onChange("show_on_all_products", event.target.checked)}
            />
          }
          label={
            <MDTypography variant="button">Show on the All Products page</MDTypography>
          }
        />
      </Grid>

      {/* -------------------------------------------------------------- Logo */}
      <Grid item xs={12}>
        <Divider />
        <MDTypography variant="h6">Logo</MDTypography>
      </Grid>

      {existingLogo ? (
        <Grid item xs={12} sm={4}>
          <MDTypography variant="caption" color="text" display="block" mb={0.5}>
            Current logo
          </MDTypography>
          <img
            src={
              existingLogo.startsWith("/uploads/")
                ? Constants.imageDomain + existingLogo
                : existingLogo
            }
            alt={values.name || "Brand logo"}
            style={{
              height: 90,
              maxWidth: "100%",
              objectFit: "contain",
              borderRadius: 6,
              border: "1px solid #e0e0e0",
              background: "#fafafa",
            }}
          />
        </Grid>
      ) : null}

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          type="file"
          label={existingLogo ? "Replace logo" : "Brand logo"}
          InputLabelProps={{ shrink: true }}
          inputProps={{ accept: "image/*" }}
          onChange={(event) => onFile(event.target.files?.[0] || null)}
          error={Boolean(errors.logo)}
          helperText={errors.logo || "JPG, PNG, WEBP or SVG up to 5 MB"}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        {text("badge", "Badge text", {
          helperText: "Pill shown when there is no logo",
        })}
      </Grid>

      <Grid item xs={12} sm={4}>
        {text("logo_class", "Logo size classes", {
          helperText: "Advanced — e.g. h-16 w-auto md:h-24",
        })}
      </Grid>

      {/* ----------------------------------------------------- Page content */}
      <Grid item xs={12}>
        <Divider />
        <MDTypography variant="h6">Page content</MDTypography>
        <MDTypography variant="caption" color="text">
          What the brand&apos;s own page shows above its product range.
        </MDTypography>
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("tagline", "Tagline", {
          helperText: "Headline under the logo",
        })}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("range_heading", "Product range heading")}
      </Grid>

      <Grid item xs={12}>
        {text("description", "Short description", {
          rows: 3,
          helperText: "Used on brand cards (the Versele-Laga sub-brand cards, for example)",
        })}
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Intro paragraph"
          helperText="Paragraphs shown under the tagline, in order."
          values={values.intro || []}
          onChange={(next) => onChange("intro", next)}
          rows={4}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("why_title", 'First card title', {
          placeholder: "Why Choose VALUE NEST?",
        })}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("commitment_title", "Second card title", {
          placeholder: "Our Commitment",
        })}
      </Grid>

      <Grid item xs={12} sm={6}>
        <StringListField
          label="First card point"
          helperText="Bullet points in the first highlight card."
          values={values.why_points || []}
          onChange={(next) => onChange("why_points", next)}
          rows={2}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <StringListField
          label="Second card paragraph"
          helperText="Paragraphs in the second highlight card."
          values={values.commitment || []}
          onChange={(next) => onChange("commitment", next)}
          rows={3}
        />
      </Grid>

      {/* ------------------------------------------------------------- Theme */}
      <Grid item xs={12}>
        <Divider />
        <MDTypography variant="h6">Page colours</MDTypography>
        <MDTypography variant="caption" color="text">
          Optional. Hex values such as #2B6CB0 — leave blank to use the site default.
        </MDTypography>
      </Grid>

      {[
        ["primary_color", "Primary colour"],
        ["secondary_color", "Secondary colour"],
        ["background_color", "Background colour"],
        ["border_color", "Border colour"],
      ].map(([name, label]) => (
        <Grid item xs={12} sm={3} key={name}>
          <MDBox display="flex" alignItems="center" gap={1}>
            {text(name, label, { placeholder: "#2B6CB0" })}
            <MDBox
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: "6px",
                border: "1px solid #e0e0e0",
                backgroundColor: /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(values[name] || "")
                  ? values[name]
                  : "transparent",
              }}
            />
          </MDBox>
        </Grid>
      ))}

      {/* --------------------------------------------------------------- SEO */}
      <Grid item xs={12}>
        <Divider />
        <MDTypography variant="h6">Search engine details</MDTypography>
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("seo_title", "Meta title")}
      </Grid>

      <Grid item xs={12} sm={6}>
        {text("seo_keywords", "Meta keywords")}
      </Grid>

      <Grid item xs={12}>
        {text("seo_description", "Meta description", { rows: 2 })}
      </Grid>
    </Grid>
  );
};

BrandFormFields.defaultProps = {
  errors: {},
  parentOptions: [],
  existingLogo: "",
  isUpdate: false,
};

BrandFormFields.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onFile: PropTypes.func.isRequired,
  parentOptions: PropTypes.arrayOf(PropTypes.object),
  existingLogo: PropTypes.string,
  isUpdate: PropTypes.bool,
};

export default BrandFormFields;
